import { FC, RefObject, useEffect, useRef } from "react";
import { useReactFlow } from "reactflow";

interface CtrlDragPanProviderProps {
  flowRef: RefObject<HTMLDivElement>;
}

/**
 * Pan the canvas by holding Ctrl + left mouse and dragging.
 *
 * ReactFlow's built-in panning runs through d3-zoom, whose filter rejects any
 * mouse event where `ctrlKey` is true (Ctrl is reserved for pinch/zoom), so
 * `panActivationKeyCode="Control"` can never drag-pan. Because d3-zoom ignores
 * those Ctrl events, this handler can own Ctrl+drag without fighting it: it
 * reads the viewport on mousedown and shifts it by the pixel delta on
 * mousemove. Applies to every workflow view since they all share FlowEditor.
 */
const CtrlDragPanProvider: FC<CtrlDragPanProviderProps> = ({ flowRef }) => {
  const { getViewport, setViewport } = useReactFlow();
  const pan = useRef({
    active: false,
    startX: 0,
    startY: 0,
    vpX: 0,
    vpY: 0,
    zoom: 1,
  });

  useEffect(() => {
    const el = flowRef.current;
    if (!el) return;

    const onMouseDown = (event: MouseEvent) => {
      if (!event.ctrlKey || event.button !== 0) return;
      const vp = getViewport();
      pan.current = {
        active: true,
        startX: event.clientX,
        startY: event.clientY,
        vpX: vp.x,
        vpY: vp.y,
        zoom: vp.zoom,
      };
      el.style.cursor = "grabbing";
      event.preventDefault();
      event.stopPropagation();
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!pan.current.active) return;
      const dx = event.clientX - pan.current.startX;
      const dy = event.clientY - pan.current.startY;
      setViewport({
        x: pan.current.vpX + dx,
        y: pan.current.vpY + dy,
        zoom: pan.current.zoom,
      });
      event.preventDefault();
    };

    const onMouseUp = () => {
      if (!pan.current.active) return;
      pan.current.active = false;
      el.style.cursor = "";
    };

    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [flowRef, getViewport, setViewport]);

  return null;
};

export default CtrlDragPanProvider;
